import React, { useState, useEffect } from "react";
import { ArrowUpRight, ShieldCheck, Activity, AlertOctagon, CheckCircle2 } from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import { usePageHeaderSlots } from "../context/PageHeaderSlotContext";
import { fetchAdsTrackingClient, fetchAdsTrackingOverview } from "../api";
import { LoadingState } from "../components/LoadingState";
import { AdsTrackingDesktop } from "../components/ads_tracking/AdsTrackingDesktop";
import { AdsTrackingMobile } from "../components/ads_tracking/AdsTrackingMobile";
import type { AdsTrackingClientPayload, AdsTrackingOverviewPayload } from "../types";
import "../components/ads_tracking/ads_tracking.css";

const DESKTOP_QUERY = "(min-width: 768px)";

function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

export function AdsTrackingPage() {
  const { selectedClient, setSelectedClient, session, data: dashboardData } = useDashboard();
  const headerSlots = usePageHeaderSlots();
  const isDesktop = useIsDesktop();

  const [overview, setOverview] = useState<AdsTrackingOverviewPayload | null>(null);
  const [clientData, setClientData] = useState<AdsTrackingClientPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const effectiveClient =
    selectedClient && selectedClient !== "ALL"
      ? selectedClient
      : (session?.tenantSlug || dashboardData?.clients?.[0]?.clientSlug || "chp");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    if (selectedClient === "ALL") {
      fetchAdsTrackingOverview()
        .then((res) => {
          if (active) setOverview(res);
        })
        .catch((err) => {
          if (active) {
            console.error("Error cargando overview de Ads Tracking:", err);
            setError(err instanceof Error ? err.message : "Error cargando resumen de cartera.");
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    } else {
      fetchAdsTrackingClient(effectiveClient)
        .then((res) => {
          if (active) setClientData(res);
        })
        .catch((err) => {
          if (active) {
            console.error("Error cargando cliente en Ads Tracking:", err);
            setError(err instanceof Error ? err.message : "Error cargando auditoría del cliente.");
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [selectedClient, effectiveClient]);

  if (loading && !overview && !clientData) {
    return <LoadingState />;
  }

  // VISTA GLOBAL DE CARTERA (CLIENTE = "ALL")
  if (selectedClient === "ALL" && overview) {
    return (
      <div className="at-wrap page" style={{ padding: "32px 24px" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div style={{ marginBottom: "24px" }}>
            <h1 style={{ fontSize: "24px", fontWeight: 700, margin: "0 0 6px" }}>
              Panel Global de Auditoría Ads & Atribución (Datos Reales)
            </h1>
            <p style={{ color: "var(--at-muted)", fontSize: "14px", margin: 0 }}>
              Consolidación de BigQuery: <code>vw_dm_budget_status_mtd</code> y <code>vw_dm_gtm_health</code>.
            </p>
          </div>

          <div className="at-kpi-grid">
            <div className="at-kpi-card">
              <div className="at-kpi-label">
                <span>Score Promedio GTM</span>
                <ShieldCheck size={16} />
              </div>
              <div className="at-kpi-val">
                {overview.portfolio_health_score !== null ? `${overview.portfolio_health_score} / 100` : "N/D"}
              </div>
              <div className="at-kpi-sub">
                {overview.healthy_clients} de {overview.total_clients} clientes con score $\ge 80$
              </div>
            </div>

            <div className="at-kpi-card">
              <div className="at-kpi-label">
                <span>Gasto MTD Cartera</span>
                <Activity size={16} />
              </div>
              <div className="at-kpi-val">
                ${Math.round(overview.spend_mtd || 0).toLocaleString("es-CL")}
              </div>
              <div className="at-kpi-sub">
                Presupuesto mes: ${Math.round(overview.budget_mtd || 0).toLocaleString("es-CL")}
              </div>
            </div>

            <div className={`at-kpi-card ${overview.critical_clients > 0 ? "is-alert" : ""}`}>
              <div className="at-kpi-label">
                <span>Clientes con Riesgo P0</span>
                <AlertOctagon size={16} />
              </div>
              <div className="at-kpi-val">{overview.critical_clients}</div>
              <div className="at-kpi-sub" style={{ color: overview.critical_clients > 0 ? "var(--at-p0)" : "inherit" }}>
                En <code>vw_dm_gtm_health</code>
              </div>
            </div>

            <div className="at-kpi-card">
              <div className="at-kpi-label">
                <span>Intradía</span>
                <CheckCircle2 size={16} />
              </div>
              <div className="at-kpi-val" style={{ fontSize: "16px", marginTop: "4px" }}>
                Sin Conector
              </div>
              <div className="at-kpi-sub">Requiere Google Ads MCP / Meta MCP</div>
            </div>
          </div>

          <div className="at-table-container">
            <div className="at-table-toolbar">
              <div style={{ fontWeight: 700, fontSize: "14px" }}>
                Clientes con Presupuesto MTD en BigQuery ({overview.clients_summary.length})
              </div>
            </div>
            {overview.clients_summary.length > 0 ? (
              <table className="at-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Health Score GTM</th>
                    <th>Pacing Intradía</th>
                    <th>Pacing Mes (MTD)</th>
                    <th>Gasto MTD</th>
                    <th>Alertas P0 / P1</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.clients_summary.map((c) => (
                    <tr
                      key={c.client_slug}
                      onClick={() => setSelectedClient(c.client_slug)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <strong>{c.display_name}</strong>
                        <small style={{ display: "block", color: "var(--at-muted)" }}>{c.client_slug}</small>
                      </td>
                      <td>
                        {c.health_score !== null ? (
                          <span className={`at-badge ${c.health_score >= 80 ? "success" : c.health_score >= 50 ? "p1" : "p0"}`}>
                            {c.health_score} / 100
                          </span>
                        ) : (
                          <span className="at-badge neutral">Sin auditar</span>
                        )}
                      </td>
                      <td>
                        <span style={{ color: "var(--at-muted)", fontSize: "12px" }}>No medido</span>
                      </td>
                      <td>
                        {c.pacing_mtd_pct !== null ? (
                          <span style={{ fontWeight: 600 }}>{c.pacing_mtd_pct}%</span>
                        ) : (
                          <span style={{ color: "var(--at-muted)" }}>Sin presupuesto</span>
                        )}
                      </td>
                      <td>
                        {c.spend_mtd !== undefined ? (
                          <span>${Math.round(c.spend_mtd).toLocaleString("es-CL")}</span>
                        ) : (
                          <span>$0</span>
                        )}
                      </td>
                      <td>
                        {c.p0_alerts > 0 ? (
                          <span className="at-badge p0">{c.p0_alerts} P0</span>
                        ) : c.p1_alerts > 0 ? (
                          <span className="at-badge p1">{c.p1_alerts} P1</span>
                        ) : (
                          <span className="at-badge success">OK</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="at-filter-btn is-active"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedClient(c.client_slug);
                          }}
                          style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          Abrir <ArrowUpRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: "32px", textAlign: "center", color: "var(--at-muted)" }}>
                No se encontraron registros en <code>vw_dm_budget_status_mtd</code>.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error && !clientData) {
    return (
      <div className="at-wrap page" style={{ padding: "32px 24px" }}>
        <div style={{ background: "var(--at-p0-bg)", border: "1px solid var(--at-p0)", padding: "16px", borderRadius: "8px", color: "var(--at-p0)" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!clientData) return null;

  return (
    <div className="at-wrap">
      {isDesktop ? (
        <AdsTrackingDesktop
          data={clientData}
          headerSlotLeft={headerSlots.left}
          headerSlotActions={headerSlots.actions}
        />
      ) : (
        <AdsTrackingMobile data={clientData} />
      )}
    </div>
  );
}
